import React, { useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { Breadcrumbs, DoughnutChart } from 'components';
import {
  useMobile,
  useReviewTalentsSummary,
  useGetAssessmentInfo,
  useOverviewAnalytics,
  useBasicExpectationAnalytics,
  useSoftSkillsAnalytics,
  useWorkStyleAnalytics,
  useWorkplaceQualitiesAnalytics,
  useWorkMotivatorsAnalytics,
  usePersonalityAnalytics,
  useDomainKnowledgeAnalytics,
} from 'hooks';
import { Spinner } from 'components/loader';
import { useCredits } from 'hooks/useCredits';
import { BuyAdditionalCreditModal } from 'pages/pricing-v2/components/buy-additional-credit-modal';
import { getScoreColor, getScoreBgClass, getScoreTextClass } from 'utils/scoreColor';
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  ResponsiveContainer,
  Cell,
  PieChart,
  Pie,
  Tooltip,
} from 'recharts';

// ==========================================
// Types
// ==========================================
interface AlignmentItem {
  label: string;
  score: number;
  status: 'Excellent' | 'Good' | 'Average' | 'Low';
  color: string;
}

interface StrengthItem {
  name: string;
  score: number;
  color: string;
}

interface CriticalGapItem {
  name: string;
  percentage: number;
  color: string;
}

// Tab configuration
const analyticsTabs = [
  { id: 'overview', label: 'Overview' },
  { id: 'basic-expectation', label: 'Basic Expectation' },
  { id: 'soft-skills', label: 'Soft Skills' },
  { id: 'work-style', label: 'Work Style' },
  { id: 'workplace-qualities', label: 'Workplace Qualities' },
  { id: 'work-motivators', label: 'Work Motivators' },
  { id: 'personality', label: 'Personality' },
  { id: 'domain-knowledge', label: 'Domain Knowledge' },
];

// ==========================================
// Helper Functions
// ==========================================
const getChartColor = (score: number): string => getScoreColor(score);

const getVerdictStatus = (verdict: string): 'Excellent' | 'Good' | 'Average' | 'Low' => {
  const verdictLower = verdict.toLowerCase();
  if (verdictLower === 'excellent' || verdictLower === 'high') return 'Excellent';
  if (verdictLower === 'good') return 'Good';
  if (verdictLower === 'average' || verdictLower === 'medium') return 'Average';
  return 'Low';
};

const formatSnakeCase = (str: string): string => {
  return str
    .split('_')
    .map(word => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase())
    .join(' ');
};

// ==========================================
// Empty State Component
// ==========================================
const EmptyState: React.FC<{ title: string; message: string }> = ({ title, message }) => (
  <div className="flex flex-col items-center justify-center py-12 px-4">
    <div className="w-16 h-16 bg-[#F2F4F7] rounded-full flex items-center justify-center mb-4">
      <svg width="32" height="32" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
        <path d="M12 8V12M12 16H12.01M22 12C22 17.5228 17.5228 22 12 22C6.47715 22 2 17.5228 2 12C2 6.47715 6.47715 2 12 2C17.5228 2 22 6.47715 22 12Z" stroke="#667085" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
      </svg>
    </div>
    <h4 className="text-base font-semibold text-[#1D2939] mb-1">{title}</h4>
    <p className="text-sm text-[#667085] text-center max-w-xs">{message}</p>
  </div>
);

// ==========================================
// Section Empty State Component
// ==========================================
const SectionEmptyState: React.FC<{ message: string }> = ({ message }) => (
  <div className="flex flex-col items-center justify-center py-8 px-4 bg-[#F9FAFB] rounded-lg border border-dashed border-[#EAECF0]">
    <p className="text-sm text-[#667085] text-center">{message}</p>
  </div>
);

// ==========================================
// Interpretation of Data Component
// ==========================================
interface InterpretationItem {
  title: string;
  description: string;
  isChecked: boolean;
}

const getInterpretation = (averageScore: number, verdict: string): InterpretationItem => {
  if (averageScore >= 80 || verdict.toLowerCase() === 'excellent') {
    return {
      title: 'Strong Match',
      description: 'The candidates show excellent alignment with your requirements. Consider moving forward with interviews for top performers.',
      isChecked: true,
    };
  } else if (averageScore >= 60 || verdict.toLowerCase() === 'good') {
    return {
      title: 'Good Potential',
      description: 'The candidates show good alignment overall. Review individual profiles to identify the best fits for your role.',
      isChecked: true,
    };
  } else if (averageScore >= 40 || verdict.toLowerCase() === 'average') {
    return {
      title: 'Expand Search',
      description: 'I recommend expanding your candidate search to explore a wider talent pool before making a final decision. This can help ensure the best possible fit for your role.',
      isChecked: true,
    };
  } else {
    return {
      title: 'Expand Search',
      description: 'I recommend expanding your candidate search to explore a wider talent pool before making a final decision. This can help ensure the best possible fit for your role.',
      isChecked: true,
    };
  }
};

const InterpretationSection: React.FC<{ averageScore: number; verdict: string }> = ({ averageScore, verdict }) => {
  const interpretation = getInterpretation(averageScore, verdict);

  return (
    <div className="mt-12">
      <div className="flex items-center cursor-pointer gap-4 py-5">
        <h3 className='sm:text-2xl text-xl whitespace-nowrap font-medium text-[#012169]'>
          Interpretation of Data
        </h3>
        <div className="h-0.5 w-[67%] bg-[#F2F4F7]"></div>
      </div>
      <div className="p-6 bg-[#F5FAFF] border border-[#D1E9FF] rounded-lg">
        <div className="flex items-start gap-3">
          <div className="flex-1">
            <div className="flex items-center gap-3 mb-[10px]">
              <span className="font-semibold text-[#1D2939]">{interpretation.title}</span>
              {interpretation.isChecked && (
                <svg width="20" height="20" viewBox="0 0 20 20" fill="none" xmlns="http://www.w3.org/2000/svg">
                  <rect width="20" height="20" rx="4" fill="#18B368"/>
                  <path d="M14.6668 6.5L8.25016 12.9167L5.3335 10" stroke="white" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                </svg>
              )}
            </div>
            <p className="text-sm text-[#475467]">{interpretation.description}</p>
          </div>
        </div>
      </div>
    </div>
  );
};

// ==========================================
// Alignment Bar Component
// ==========================================
const AlignmentBar: React.FC<{ item: AlignmentItem }> = ({ item }) => {
  const getBarColor = getScoreBgClass;

  return (
    <div className="flex items-center gap-2 md:gap-4">
      <div className="w-full flex items-center gap-4">
        <div className="w-full h-2 relative">
          <div
            className="left-0 absolute z-30 sm:-top-[19px] -top-[16px]"
            style={{
              width: `${Math.max(item.score, 1)}%`,
            }}
          >
            <p className='text-[#344054] sm:font-medium sm:text-[10px] text-[8px] whitespace-nowrap mb-1 ml-auto w-fit'>{item.status}</p>
            <div
              className={`rounded-r-full h-2 ${getBarColor(item.score)}`}
              style={{ minWidth: item.score > 0 ? '8px' : '0px' }}
            />
          </div>
          <div className='bg-[#EAECF0] h-2 rounded-r-full' />
        </div>
        <span className="text-xs text-[#344054] min-w-[40px] text-right">
          {item.score}%
        </span>
      </div>
    </div>
  );
};

// ==========================================
// Strength Bar Chart Component (Recharts)
// ==========================================
const StrengthBarChart: React.FC<{ data: StrengthItem[]; totalCandidates?: number; title?: string }> = ({
  data,
  totalCandidates = 0,
  title = 'Strength'
}) => {
  const avgScore = data.length > 0 ? Math.round(data.reduce((acc, d) => acc + d.score, 0) / data.length) : 0;

  if (data.length === 0) {
    return (
      <div className="border border-[#EAECF0] p-6 rounded-2xl">
        <h3 className="text-lg font-semibold text-[#1D2939] mb-4">{title}</h3>
        <SectionEmptyState message="No data available for this section" />
      </div>
    );
  }

  const chartData = data.map(item => ({
    name: item.name,
    score: item.score,
    fill: item.color,
  }));

  return (
    <div className="border border-[#EAECF0] p-6 rounded-2xl">
      <h3 className="text-lg font-semibold text-[#1D2939] mb-4">{title}</h3>
      <div className="relative">
        <ResponsiveContainer width="100%" height={220}>
          <BarChart data={chartData} margin={{ top: 20, right: 10, left: 10, bottom: 60 }}>
            <XAxis 
              dataKey="name" 
              tick={{ fontSize: 10, fill: '#667085' }}
              angle={-45}
              textAnchor="end"
              interval={0}
              height={60}
            />
            <YAxis 
              tick={{ fontSize: 10, fill: '#667085' }}
              domain={[0, 100]}
              tickFormatter={(value) => `${value}%`}
            />
            <Tooltip 
              formatter={(value) => [`${value}%`, 'Score']}
              contentStyle={{ fontSize: 12 }}
            />
            <Bar dataKey="score" radius={[4, 4, 0, 0]}>
              {chartData.map((entry, index) => (
                <Cell key={`cell-${index}`} fill={entry.fill} />
              ))}
            </Bar>
          </BarChart>
        </ResponsiveContainer>
        <div className="absolute top-2 right-2 bg-white/90 px-2 py-1 rounded text-xs font-semibold">
          <span className={getScoreTextClass(avgScore)}>{avgScore}% avg.</span>
          <br />
          <span className="text-[10px] text-gray-500">{totalCandidates} Candidates</span>
        </div>
      </div>
    </div>
  );
};

// ==========================================
// Critical Gap Pie Chart Component (Recharts)
// ==========================================
const CriticalGapPieChart: React.FC<{ data: CriticalGapItem[]; totalCandidates?: number; title?: string }> = ({
  data,
  totalCandidates = 0,
  title = 'Critical Gap'
}) => {
  if (data.length === 0) {
    return (
      <div className="border border-[#EAECF0] p-6 rounded-2xl">
        <h3 className="text-lg font-semibold text-[#1D2939] mb-4">{title}</h3>
        <SectionEmptyState message="No critical gaps identified" />
      </div>
    );
  }

  const chartData = data.map(item => ({
    name: item.name,
    value: item.percentage,
    fill: item.color,
  }));

  return (
    <div className="border border-[#EAECF0] p-6 rounded-2xl">
      <h3 className="text-lg font-semibold text-[#1D2939] mb-4">{title}</h3>
      <div className="flex flex-col justify-center gap-6 max-[480px]:flex-col">
        <div className="relative w-[180px] h-[180px] mx-auto my-2">
          <ResponsiveContainer width="100%" height="100%">
            <PieChart>
              <Pie
                data={chartData}
                cx="50%"
                cy="50%"
                innerRadius={40}
                outerRadius={80}
                paddingAngle={0}
                dataKey="value"
                stroke="transparent"
              >
                {chartData.map((entry, index) => (
                  <Cell key={`cell-${index}`} fill={entry.fill} />
                ))}
              </Pie>
              <Tooltip formatter={(value) => [`${Number(value).toFixed(1)}%`, 'Percentage']} />
            </PieChart>
          </ResponsiveContainer>
          <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 text-2xl font-bold text-[#1D2939]">
            {totalCandidates}
          </div>
        </div>
        <div className="flex flex-col gap-2">
          {data.map((item, index) => (
            <div key={index} className="flex items-center gap-2 text-xs text-[#475467]">
              <div
                className="w-3 h-3 rounded-sm flex-shrink-0"
                style={{ backgroundColor: item.color }}
              />
              <span className="truncate">{item.name} ({item.percentage.toFixed(1)}%)</span>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
};

// ==========================================
// Overview Tab Component
// ==========================================
interface OverviewTabProps {
  assessmentId?: string;
}

const OverviewTab: React.FC<OverviewTabProps> = ({ assessmentId }) => {
  const { overviewAnalytics, isLoading } = useOverviewAnalytics(assessmentId);

  if (isLoading) {
    return (
      <div className="flex justify-center items-center py-20">
        <Spinner />
      </div>
    );
  }

  if (!overviewAnalytics) {
    return <EmptyState title="No Data Available" message="Overview analytics data is not available for this assessment." />;
  }

  const alignmentData: AlignmentItem[] = overviewAnalytics.alignment_overview?.map((item) => ({
    label: item.category,
    score: item.average_score,
    status: getVerdictStatus(item.verdict),
    color: getChartColor(item.average_score),
  })) || [];

  const strengthData: StrengthItem[] = overviewAnalytics.strengths?.map((item) => ({
    name: formatSnakeCase(item.name),
    score: item.average_score,
    color: getChartColor(item.average_score),
  })) || [];

  const gapData: CriticalGapItem[] = overviewAnalytics.gaps?.map((item, index) => ({
    name: formatSnakeCase(item.name),
    percentage: item.gap_percentage,
    color: index === 0 ? '#CC0000' : index === 1 ? '#FF696D' : index === 2 ? '#FFC2C4' : '#FFE6E6',
  })) || [];

  return (
    <div className="min-h-[400px]">
      {alignmentData.length > 0 ? (
        <div className="mb-8">
          <h2 className="text-xl font-semibold text-[#1D2939] mb-6">Alignment Overview</h2>
          <div className="grid sm:grid-cols-[200px_1fr] grid-cols-[130px_1fr] pt-2">
            <div className='border-r-2 border-[#D0D5DD] space-y-3 py-3 -mt-3.5 pr-4'>
              {alignmentData.map((item, index) => (
                <div key={index} className="text-right mx-auto w-fit font-medium text-[#1D2939] sm:text-[13px] text-[11px] h-[31px] flex items-center">
                  {item.label}
                </div>
              ))}
            </div>
            <div className='space-y-[31px]'>
              {alignmentData.map((item, index) => (
                <AlignmentBar key={index} item={item} />
              ))}
            </div>
          </div>
        </div>
      ) : (
        <div className="mb-8">
          <h2 className="text-xl font-semibold text-[#1D2939] mb-6">Alignment Overview</h2>
          <SectionEmptyState message="No alignment data available" />
        </div>
      )}
      
      <div className="grid grid-cols-[1.5fr_1fr] gap-8 my-12 max-md:grid-cols-1">
        <StrengthBarChart data={strengthData} totalCandidates={overviewAnalytics.total_candidates} />
        <CriticalGapPieChart data={gapData} totalCandidates={overviewAnalytics.total_candidates} />
      </div>

      <InterpretationSection
        averageScore={alignmentData.length > 0 ? Math.round(alignmentData.reduce((sum, item) => sum + item.score, 0) / alignmentData.length) : 0}
        verdict={alignmentData.length > 0 ? (alignmentData.reduce((sum, item) => sum + item.score, 0) / alignmentData.length >= 60 ? 'Good' : 'Average') : 'Low'}
      />
    </div>
  );
};

// ==========================================
// Basic Expectation Tab Component
// ==========================================
interface BasicExpectationTabProps {
  assessmentId?: string;
}

const BasicExpectationTab: React.FC<BasicExpectationTabProps> = ({ assessmentId }) => {
  const { basicExpectationAnalytics, isLoading } = useBasicExpectationAnalytics(assessmentId);

  if (isLoading) {
    return (
      <div className="flex justify-center items-center py-20">
        <Spinner />
      </div>
    );
  }

  if (!basicExpectationAnalytics) {
    return <EmptyState title="No Data Available" message="Basic expectation analytics data is not available for this assessment." />;
  }

  const alignmentData: AlignmentItem[] = basicExpectationAnalytics.breakdown?.map((item) => ({
    label: item.label,
    score: item.match_percentage,
    status: getVerdictStatus(item.match_percentage >= 80 ? 'Excellent' : item.match_percentage >= 60 ? 'Good' : item.match_percentage >= 40 ? 'Average' : 'Low'),
    color: getChartColor(item.match_percentage),
  })) || [];

  return (
    <div className="min-h-[400px]">
      {alignmentData.length > 0 ? (
        <div className="mb-8">
          <h2 className="text-xl font-semibold text-[#1D2939] mb-6">Alignment Overview</h2>
          <div className="grid sm:grid-cols-[200px_1fr] grid-cols-[130px_1fr] pt-2">
            <div className='border-r-2 border-[#D0D5DD] space-y-3 py-3 -mt-3.5 pr-4'>
              {alignmentData.map((item, index) => (
                <div key={index} className="text-right mx-auto w-fit font-medium text-[#1D2939] sm:text-[13px] text-[11px] h-[31px] flex items-center">
                  {item.label}
                </div>
              ))}
            </div>
            <div className='space-y-[31px]'>
              {alignmentData.map((item, index) => (
                <AlignmentBar key={index} item={item} />
              ))}
            </div>
          </div>
        </div>
      ) : (
        <SectionEmptyState message="No expectation breakdown available" />
      )}

      <InterpretationSection
        averageScore={basicExpectationAnalytics.average_score}
        verdict={basicExpectationAnalytics.verdict}
      />
    </div>
  );
};

// ==========================================
// Soft Skills Tab Component
// ==========================================
interface SoftSkillsTabProps {
  assessmentId?: string;
}

const SoftSkillsTab: React.FC<SoftSkillsTabProps> = ({ assessmentId }) => {
  const { softSkillsAnalytics, isLoading } = useSoftSkillsAnalytics(assessmentId);

  if (isLoading) {
    return (
      <div className="flex justify-center items-center py-20">
        <Spinner />
      </div>
    );
  }

  if (!softSkillsAnalytics) {
    return <EmptyState title="No Soft Skills Data" message="No soft skill results found for this assessment. Candidates may not have completed this section yet." />;
  }

  const strengthData: StrengthItem[] = softSkillsAnalytics.strengths?.map((item) => ({
    name: formatSnakeCase(item.name),
    score: item.average_score,
    color: getChartColor(item.average_score),
  })) || [];

  const gapData: CriticalGapItem[] = softSkillsAnalytics.gaps?.map((item, index) => ({
    name: formatSnakeCase(item.name),
    percentage: item.gap_percentage,
    color: index === 0 ? '#E74444' : index === 1 ? '#F79009' : '#FECDCA',
  })) || [];

  return (
    <div className="min-h-[400px]">
      <div className="grid grid-cols-[1.5fr_1fr] gap-8 my-8 max-md:grid-cols-1">
        <StrengthBarChart data={strengthData} totalCandidates={softSkillsAnalytics.total_candidates} />
        <CriticalGapPieChart data={gapData} totalCandidates={softSkillsAnalytics.total_candidates} />
      </div>

      <InterpretationSection
        averageScore={softSkillsAnalytics.average_score}
        verdict={softSkillsAnalytics.verdict}
      />
    </div>
  );
};

// ==========================================
// Work Style Tab Component
// ==========================================
interface WorkStyleTabProps {
  assessmentId?: string;
}

const WorkStyleTab: React.FC<WorkStyleTabProps> = ({ assessmentId }) => {
  const { workStyleAnalytics, isLoading } = useWorkStyleAnalytics(assessmentId);

  if (isLoading) {
    return (
      <div className="flex justify-center items-center py-20">
        <Spinner />
      </div>
    );
  }

  if (!workStyleAnalytics) {
    return <EmptyState title="No Work Style Data" message="Work style analytics data is not available for this assessment." />;
  }

  // Transform work_styles object to array
  const workStylesArray: { name: string; average_score: number }[] = workStyleAnalytics.work_styles 
    ? Object.values(workStyleAnalytics.work_styles)
    : [];
  
  const strengthData: StrengthItem[] = workStylesArray.map((item) => ({
    name: item.name,
    score: item.average_score,
    color: getChartColor(item.average_score),
  }));

  const gapData: CriticalGapItem[] = (workStyleAnalytics.gaps || []).map((item, index) => ({
    name: item.name,
    percentage: 100 - item.average_score,
    color: index === 0 ? '#E74444' : index === 1 ? '#F79009' : '#FECDCA',
  }));

  return (
    <div className="min-h-[400px]">
      <div className="grid grid-cols-[1.5fr_1fr] gap-8 my-8 max-md:grid-cols-1">
        <StrengthBarChart data={strengthData} totalCandidates={workStyleAnalytics.total_candidates} />
        <CriticalGapPieChart data={gapData} totalCandidates={workStyleAnalytics.total_candidates} />
      </div>

      <InterpretationSection
        averageScore={workStyleAnalytics.average_overall_score}
        verdict={workStyleAnalytics.verdict}
      />
    </div>
  );
};

// ==========================================
// Workplace Qualities Tab Component
// ==========================================
interface WorkplaceQualitiesTabProps {
  assessmentId?: string;
}

const WorkplaceQualitiesTab: React.FC<WorkplaceQualitiesTabProps> = ({ assessmentId }) => {
  const { workplaceQualitiesAnalytics, isLoading } = useWorkplaceQualitiesAnalytics(assessmentId);

  if (isLoading) {
    return (
      <div className="flex justify-center items-center py-20">
        <Spinner />
      </div>
    );
  }

  if (!workplaceQualitiesAnalytics) {
    return <EmptyState title="No Workplace Qualities Data" message="Workplace qualities analytics data is not available for this assessment." />;
  }

  // Use strengths for the bar chart (showing match rates)
  const strengthData: StrengthItem[] = (workplaceQualitiesAnalytics.strengths || []).map((item) => ({
    name: formatSnakeCase(item.title),
    score: item.match_rate,
    color: getChartColor(item.match_rate),
  }));

  // Use gaps from API for the pie chart (gaps have gap_percentage directly)
  const gapData: CriticalGapItem[] = (workplaceQualitiesAnalytics.gaps || []).map((item, index) => ({
    name: formatSnakeCase(item.title),
    percentage: item.gap_percentage,
    color: index === 0 ? '#E74444' : index === 1 ? '#F79009' : '#FECDCA',
  }));

  return (
    <div className="min-h-[400px]">
      <div className="grid grid-cols-[1.5fr_1fr] gap-8 my-8 max-md:grid-cols-1">
        <StrengthBarChart data={strengthData} totalCandidates={workplaceQualitiesAnalytics.total_candidates} />
        <CriticalGapPieChart data={gapData} totalCandidates={workplaceQualitiesAnalytics.total_candidates} />
      </div>

      <InterpretationSection
        averageScore={workplaceQualitiesAnalytics.average_score}
        verdict={workplaceQualitiesAnalytics.verdict}
      />
    </div>
  );
};

// ==========================================
// Work Motivators Tab Component
// ==========================================
interface WorkMotivatorsTabProps {
  assessmentId?: string;
}

const WorkMotivatorsTab: React.FC<WorkMotivatorsTabProps> = ({ assessmentId }) => {
  const { workMotivatorsAnalytics, isLoading } = useWorkMotivatorsAnalytics(assessmentId);

  if (isLoading) {
    return (
      <div className="flex justify-center items-center py-20">
        <Spinner />
      </div>
    );
  }

  if (!workMotivatorsAnalytics) {
    return <EmptyState title="No Work Motivators Data" message="Work motivators analytics data is not available for this assessment." />;
  }

  // Use strengths for the bar chart
  const strengthData: StrengthItem[] = (workMotivatorsAnalytics.strengths || []).map((item) => ({
    name: item.name,
    score: item.match_rate,
    color: getChartColor(item.match_rate),
  }));

  // Use gaps from API for the pie chart (gaps have gap_percentage directly)
  const gapData: CriticalGapItem[] = (workMotivatorsAnalytics.gaps || []).map((item, index) => ({
    name: item.name,
    percentage: item.gap_percentage,
    color: index === 0 ? '#E74444' : index === 1 ? '#F79009' : '#FECDCA',
  }));

  return (
    <div className="min-h-[400px]">
      <div className="grid grid-cols-[1.5fr_1fr] gap-8 my-8 max-md:grid-cols-1">
        <StrengthBarChart data={strengthData} totalCandidates={workMotivatorsAnalytics.total_candidates} />
        <CriticalGapPieChart data={gapData} totalCandidates={workMotivatorsAnalytics.total_candidates} />
      </div>

      <InterpretationSection
        averageScore={workMotivatorsAnalytics.average_score}
        verdict={workMotivatorsAnalytics.verdict}
      />
    </div>
  );
};

// ==========================================
// Personality Tab Component
// ==========================================
interface PersonalityTabProps {
  assessmentId?: string;
}

const PersonalityTab: React.FC<PersonalityTabProps> = ({ assessmentId }) => {
  const { personalityAnalytics, isLoading } = usePersonalityAnalytics(assessmentId);

  if (isLoading) {
    return (
      <div className="flex justify-center items-center py-20">
        <Spinner />
      </div>
    );
  }

  if (!personalityAnalytics) {
    return <EmptyState title="No Personality Data" message="Personality analytics data is not available for this assessment." />;
  }

  // Transform core areas (array format from API) to chart data
  const coreAreasData: StrengthItem[] = (personalityAnalytics.core_areas || []).map((item) => ({
    name: item.name,
    score: item.average_score,
    color: getChartColor(item.average_score),
  }));

  // Use gaps from API for the pie chart
  const gapData: CriticalGapItem[] = (personalityAnalytics.gaps || []).map((item, index) => ({
    name: item.name,
    percentage: item.gap_percentage,
    color: index === 0 ? '#E74444' : index === 1 ? '#F79009' : index === 2 ? '#FEDF89' : '#FEF0C7',
  }));

  return (
    <div className="min-h-[400px]">
      <div className="grid grid-cols-[1.5fr_1fr] gap-8 my-8 max-md:grid-cols-1">
        <StrengthBarChart data={coreAreasData} totalCandidates={personalityAnalytics.total_candidates} />
        <CriticalGapPieChart data={gapData} totalCandidates={personalityAnalytics.total_candidates} />
      </div>

      <InterpretationSection
        averageScore={personalityAnalytics.average_score}
        verdict={personalityAnalytics.verdict}
      />
    </div>
  );
};

// ==========================================
// Domain Knowledge Tab Component
// ==========================================
interface DomainKnowledgeTabProps {
  assessmentId?: string;
}

const DomainKnowledgeTab: React.FC<DomainKnowledgeTabProps> = ({ assessmentId }) => {
  const { domainKnowledgeAnalytics, isLoading } = useDomainKnowledgeAnalytics(assessmentId);

  if (isLoading) {
    return (
      <div className="flex justify-center items-center py-20">
        <Spinner />
      </div>
    );
  }

  if (!domainKnowledgeAnalytics) {
    return <EmptyState title="No Domain Knowledge Data" message="Domain knowledge analytics data is not available for this assessment." />;
  }

  // Transform strengths to chart data (using average_percentage for scores)
  const strengthData: StrengthItem[] = (domainKnowledgeAnalytics.strengths || []).map((item) => ({
    name: item.title,
    score: item.average_percentage,
    color: getChartColor(item.average_percentage),
  }));

  // Transform gaps to pie chart data
  const gapData: CriticalGapItem[] = (domainKnowledgeAnalytics.gaps || []).map((item, index) => ({
    name: item.title,
    percentage: item.gap_percentage,
    color: index === 0 ? '#E74444' : index === 1 ? '#F79009' : index === 2 ? '#FEDF89' : '#FEF0C7',
  }));

  return (
    <div className="min-h-[400px]">
      <div className="grid grid-cols-[1.5fr_1fr] gap-8 my-8 max-md:grid-cols-1">
        <StrengthBarChart data={strengthData} totalCandidates={domainKnowledgeAnalytics.total_candidates} />
        <CriticalGapPieChart data={gapData} totalCandidates={domainKnowledgeAnalytics.total_candidates} />
      </div>

      <InterpretationSection
        averageScore={domainKnowledgeAnalytics.average_percentage}
        verdict={domainKnowledgeAnalytics.verdict}
      />
    </div>
  );
};

// ==========================================
// Main Analytics Component
// ==========================================
const AnalyticsUI: React.FC = () => {
  const { id: assessmentId } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const [activeTab, setActiveTab] = useState('overview');
  const [showTopupModal, setShowTopupModal] = useState(false);

  // Analytics is available to every account — only the credit balance gates it.
  const { balance: credits, getCostByKeyword, canAfford } = useCredits();
  const analyticsCreditCost = getCostByKeyword('analytics', 5);
  const isGated = !canAfford(analyticsCreditCost);

  // Fetch data
  const { summary, isLoading: summaryLoading } = useReviewTalentsSummary(assessmentId);
  const { assessmentInfo, isLoading: assessmentLoading } = useGetAssessmentInfo(assessmentId || null);

  // Get assessment title and date from assessmentInfo
  const currentJobTitle = assessmentInfo?.data?.title || 'Assessment';
  const jobDate = assessmentInfo?.data?.created_at
    ? new Date(assessmentInfo.data.created_at).toLocaleDateString('en-US', {
        day: '2-digit',
        month: '2-digit',
        year: 'numeric',
      })
    : '';

  const handleViewReports = () => {
    if (assessmentId) {
      navigate(`/employer/manage-jobs/review-talent/${assessmentId}`);
    }
  };

  const renderTabContent = () => {
    switch (activeTab) {
      case 'overview':
        return <OverviewTab assessmentId={assessmentId} />;
      case 'basic-expectation':
        return <BasicExpectationTab assessmentId={assessmentId} />;
      case 'soft-skills':
        return <SoftSkillsTab assessmentId={assessmentId} />;
      case 'work-style':
        return <WorkStyleTab assessmentId={assessmentId} />;
      case 'workplace-qualities':
        return <WorkplaceQualitiesTab assessmentId={assessmentId} />;
      case 'work-motivators':
        return <WorkMotivatorsTab assessmentId={assessmentId} />;
      case 'personality':
        return <PersonalityTab assessmentId={assessmentId} />;
      case 'domain-knowledge':
        return <DomainKnowledgeTab assessmentId={assessmentId} />;
      default:
        return <OverviewTab assessmentId={assessmentId} />;
    }
  };

  if (summaryLoading || assessmentLoading) {
    return (
      <div className="p-0 overflow-hidden h-screen w-full">
        <section className="md:px-9 px-4 md:py-5 py-6 overflow-auto h-[calc(100vh-78px-var(--credit-banner-h,0px))] [scrollbar-width:none] bg-[#F5FAFF]">
          <div className="flex justify-center items-center py-20">
            <Spinner />
          </div>
        </section>
      </div>
    );
  }

  const averageScore = summary?.average_score || 0;
  const totalTalents = summary?.total_talents || 0;

  const decisionBuckets = summary?.bucket_summary
    ? Object.values(summary.bucket_summary)
        .sort((a, b) => a.from - b.from)
        .map((bucket) => ({
          label: bucket.decision,
          count: bucket.count,
          isRejected: bucket.decision.toLowerCase().includes('reject'),
        }))
    : [
        { label: 'Interview', count: summary?.interview_ready || 0, isRejected: false },
        { label: 'Probe further', count: summary?.probe_further || 0, isRejected: false },
        { label: 'Save for future', count: 0, isRejected: false },
        { label: 'Rejected', count: summary?.rejected || 0, isRejected: true },
      ];

  return (
    <div className="p-0 overflow-hidden h-screen w-full">
      <section className="md:px-9 px-4 md:py-5 py-6 overflow-auto h-[calc(100vh-78px-var(--credit-banner-h,0px))] [scrollbar-width:none] bg-[#F5FAFF]">
        <Breadcrumbs />

        {/* Header */}
        <div className="flex justify-between items-center gap-4 flex-wrap max-sm:flex-col max-sm:items-start">
          <div className="flex items-center gap-3 flex-wrap mt-10">
            <button className="flex items-center gap-2 px-4 py-2 bg-[#012169] text-white rounded-lg font-medium text-sm hover:bg-[#001a4d]">
              {currentJobTitle || 'Product Manager'}
            </button>
            <div className="flex items-center gap-3">
              <h1 className="text-2xl font-semibold text-[#101828] max-sm:text-xl">Analytics</h1>
              <span className="inline-flex items-center justify-center px-3 py-1 bg-[#012169] text-white rounded-2xl text-sm font-medium">{totalTalents}</span>
            </div>
          </div>
        </div>

        {/* Main Content */}
        <div className="relative flex gap-6 mt-6 max-lg:flex-col" style={isGated ? { overflow: 'hidden', maxHeight: '70vh' } : undefined}>
          {/* Gating overlay for insufficient credits */}
          {isGated && (
            <>
              <div className="absolute inset-0 z-[50] backdrop-blur-md bg-white/70 rounded-xl" style={{ isolation: 'isolate' }} />
              <div className="absolute inset-0 z-[60] flex items-center justify-center">
                <div
                  className="bg-white rounded-2xl p-8 max-w-[440px] w-[90%] text-center"
                  style={{ boxShadow: '0 20px 60px rgba(0, 0, 0, 0.15)' }}
                >
                  <h3 className="text-xl font-bold text-[#101828] mb-2">Unlock Analytics</h3>
                  <p className="text-sm text-[#475467] leading-relaxed mb-6">
                    You need at least {analyticsCreditCost} credits to view analytics. Top up your credits to
                    continue.
                  </p>
                  <button
                    onClick={() => setShowTopupModal(true)}
                    className="w-full py-3 bg-[#012169] text-white font-medium rounded-lg hover:bg-[#011557] transition-colors"
                  >
                    Topup Credit
                  </button>
                </div>
              </div>
            </>
          )}

          {/* Sidebar */}
          <aside className="w-[280px] shrink-0 bg-white rounded-xl p-6 shadow-[0_1px_3px_rgba(16,24,40,0.1)] h-fit sticky top-4 max-lg:w-full max-lg:relative max-lg:top-0">
            {/* Score Circle */}
            <div className="flex flex-col items-center mb-6">
              <div className="relative w-[120px] h-[120px]">
                <DoughnutChart
                  score={averageScore}
                  total={100}
                  cutout="75%"
                  className="w-full h-full"
                />
                <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 text-center">
                  <p className={`text-2xl font-bold ${getScoreTextClass(averageScore)}`}>
                    {averageScore}%
                  </p>
                  <p className="text-xs text-[#667085]">avg.</p>
                </div>
              </div>
            </div>

            {/* Job Info */}
            <div className="text-center pb-6 border-b border-[#EAECF0] mb-6">
              <h2 className="text-lg font-semibold text-[#1D2939] mb-1">{currentJobTitle || 'Product Manager'}</h2>
              <p className="text-sm text-[#667085]">{jobDate || ''}</p>
            </div>

            {/* Decisions */}
            <div>
              <h3 className="text-base font-semibold text-[#1D2939] mb-4">Decisions</h3>
              {decisionBuckets.map((decision, index) => (
                <div key={index} className="flex justify-between items-center py-2">
                  <span className="text-sm text-[#475467]">{decision.label}</span>
                  <span
                    className={`text-sm font-semibold ${decision.isRejected ? 'text-[#E74444]' : 'text-[#175CD3]'}`}
                  >
                    {decision.count}
                  </span>
                </div>
              ))}
            </div>

            {/* View Reports Link */}
            <a onClick={handleViewReports} className="block text-center mt-4 text-sm text-[#175CD3] underline cursor-pointer hover:text-[#1849a0]">
              View Reports
            </a>
          </aside>

          {/* Main Content Area */}
          <main className="flex-1 min-w-0 bg-white rounded-xl p-6 shadow-[0_1px_3px_rgba(16,24,40,0.1)]">
            {/* Tabs */}
            <div className="flex gap-2 border-b border-[#EAECF0] mb-6 overflow-x-auto [scrollbar-width:none] [&::-webkit-scrollbar]:hidden">
              {analyticsTabs.map((tab) => (
                <button
                  key={tab.id}
                  className={`!bg-transparent !rounded-none px-4 py-3 text-sm font-medium cursor-pointer whitespace-nowrap border-b-2 transition-all ${activeTab === tab.id
                    ? 'text-[#012169] border-[#012169]'
                    : 'text-[#667085] border-transparent hover:text-[#012169]'
                    }`}
                  onClick={() => setActiveTab(tab.id)}
                >
                  {tab.label}
                </button>
              ))}
            </div>

            {/* Tab Content */}
            {renderTabContent()}
          </main>
        </div>
      </section>

      <BuyAdditionalCreditModal
        isOpen={showTopupModal}
        onClose={() => setShowTopupModal(false)}
        currentBalance={credits}
      />
    </div>
  );
};

export { AnalyticsUI };
